import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { fetchLocation, updateLocation, setLocationDisplay } from "../../actions/currentLocation";
import { updateSelectedCuisines } from "../../actions/restaurants";
import { getLatLong } from "../../util/geoUtils";
import FilterChips from "./FilterChips";
import "./UnifiedSearchBar.css";

/**
 * UnifiedSearchBar - Collapsible search
 * Collapsed: Location pill
 * Expanded: Location input + cuisine/keyword search
 */
const UnifiedSearchBar = ({
  mapCenter,
  locationDisplay,
  updateLocation,
  fetchLocation,
  setLocationDisplay,
  selectedCuisines,
  updateSelectedCuisines,
  adHocSearchActive,
  restaurants,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Refs
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const locationInputRef = useRef(null);

  // Close when clicking outside
  useEffect(function setupClickOutsideListener() {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsExpanded(false);
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // No auto-focus when expanding - user must click to focus a field

  // Get cuisines from ALL restaurants in the area (unfiltered by keyword/leaf/cuisine filters)
  // This ensures the cuisine dropdown remains useful even when other filters are active
  const resultCuisines = useMemo(() => {
    const cuisineSet = new Set();
    (restaurants || []).forEach((restaurant) => {
      (restaurant.cuisine || []).forEach((c) => {
        if (c && c.trim()) cuisineSet.add(c);
      });
    });
    return Array.from(cuisineSet).sort((a, b) => a.localeCompare(b));
  }, [restaurants]);

  // Generate suggestions based on search text
  useEffect(function generateSuggestionsFromSearchText() {
    const newSuggestions = [];

    if (!searchText.trim()) {
      // No search text - show cuisines available in current results only
      if (resultCuisines.length > 0) {
        newSuggestions.push({ type: "header", label: "Available Nearby" });
        resultCuisines.forEach((cuisine) => {
          newSuggestions.push({ type: "cuisine", value: cuisine, label: cuisine });
        });
      } else {
        // No restaurants in results - show hint to search
        newSuggestions.push({ type: "header", label: "Search" });
        newSuggestions.push({
          type: "hint",
          label: "Type to search menu items",
        });
      }

      setSuggestions(newSuggestions);
      return;
    }

    const text = searchText.toLowerCase().trim();

    // Show matching cuisines from current results only
    const matchingResultCuisines = resultCuisines.filter((c) =>
      c.toLowerCase().includes(text)
    );

    if (matchingResultCuisines.length > 0) {
      newSuggestions.push({ type: "header", label: "Available Nearby" });
      matchingResultCuisines.forEach((cuisine) => {
        newSuggestions.push({ type: "cuisine", value: cuisine, label: cuisine });
      });
    }

    // Always show keyword search option when typing
    newSuggestions.push({ type: "header", label: "Menu Search" });
    newSuggestions.push({
      type: "keyword",
      value: searchText,
      label: `Search menus for "${searchText}"`,
    });

    setSuggestions(newSuggestions);
  }, [searchText, resultCuisines]);

  // Get location display text
  const getLocationText = () => {
    if (locationDisplay) {
      if (locationDisplay.type === "gps") return "Near me";
      if (locationDisplay.name) return locationDisplay.name;
    }
    if (mapCenter?.lat && mapCenter?.lng) {
      return `${mapCenter.lat.toFixed(2)}, ${mapCenter.lng.toFixed(2)}`;
    }
    return "Set location";
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === "cuisine") {
      const newCuisines = {
        ...selectedCuisines,
        [suggestion.value]: suggestion.value,
      };
      updateSelectedCuisines(newCuisines);
      // Sync cuisine to URL
      const currentParams = new URLSearchParams(location.search);
      const cuisineStr = Object.values(newCuisines).filter(Boolean).join(",");
      if (cuisineStr) {
        currentParams.set("cuisine", cuisineStr);
      } else {
        currentParams.delete("cuisine");
      }
      navigate(`?${currentParams.toString()}`);
      setSearchText("");
    } else if (suggestion.type === "keyword") {
      // Update URL - backend handles keyword search for both regular and ad hoc results
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("keywords", suggestion.value);
      navigate(`?${currentParams.toString()}`);
      setSearchText("");
    }
    setIsDropdownOpen(false);
    setIsExpanded(false);
  };

  // Handle Enter key in search
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchText.trim()) {
      e.preventDefault();
      // Update URL - backend handles keyword search for both regular and ad hoc results
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("keywords", searchText.trim());
      navigate(`?${currentParams.toString()}`);
      setSearchText("");
      setIsDropdownOpen(false);
      setIsExpanded(false);
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setSearchText("");
      setIsExpanded(false);
    }
  };

  // Handle location submit
  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    if (!locationInput.trim()) return;

    setIsSearching(true);
    try {
      const { lat, lng } = (await getLatLong(locationInput.trim())).data;
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("lat", lat.toString());
      currentParams.set("lng", lng.toString());
      navigate(`?${currentParams.toString()}`);

      await updateLocation({ lat, lng });
      setLocationDisplay("typed", locationInput.trim());
      setLocationInput("");
      // Keep expanded, focus search input
      if (searchInputRef.current) searchInputRef.current.focus();
    } catch (error) {
      console.error("Error geocoding location:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle "Use my location"
  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      fetchLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const currentParams = new URLSearchParams(location.search);
          currentParams.set("lat", latitude.toString());
          currentParams.set("lng", longitude.toString());
          navigate(`?${currentParams.toString()}`);

          updateLocation({ lat: latitude, lng: longitude });
          setLocationDisplay("gps", null);
          // Keep expanded, focus search input
          if (searchInputRef.current) searchInputRef.current.focus();
        },
        (error) => {
          console.error("Error getting location:", error);
          fetchLocation(false);
        }
      );
    }
  };

  const hasLocation = mapCenter?.lat && mapCenter?.lng;

  // COLLAPSED VIEW - just a location pill
  if (!isExpanded) {
    return (
      <div
        className="unified-search-container"
        ref={containerRef}
      >
        <button
          type="button"
          className="unified-search-collapsed"
          onClick={() => setIsExpanded(true)}
        >
          <i className="fas fa-map-marker-alt" />
          <span>{getLocationText()}</span>
          <i className="fas fa-search unified-search-collapsed-icon" />
        </button>
      </div>
    );
  }

  // EXPANDED VIEW - full search
  return (
    <div
      className="unified-search-container"
      ref={containerRef}
    >
      <div className="unified-search-expanded">
        {/* Search row */}
        <div className="unified-search-row">
          {/* Location section - disabled during ad hoc search */}
          <div
            className={`unified-search-location-section ${adHocSearchActive ? "is-disabled" : ""}`}
            title={adHocSearchActive ? "Location is locked during discovery search" : undefined}
          >
            <form onSubmit={handleLocationSubmit} className="unified-search-location-form">
              <i className="fas fa-map-marker-alt unified-search-location-icon" />
              <input
                ref={locationInputRef}
                type="text"
                className="unified-search-location-input"
                placeholder={hasLocation ? getLocationText() : "City or zip..."}
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                disabled={isSearching || adHocSearchActive}
                title={adHocSearchActive ? "Location is locked during discovery search" : undefined}
              />
            </form>
            <button
              type="button"
              className="unified-search-btn"
              onClick={handleUseMyLocation}
              title={adHocSearchActive ? "Location is locked during discovery search" : "Use my location"}
              disabled={adHocSearchActive}
            >
              <i className="fas fa-location-arrow" />
            </button>
          </div>

          {/* Divider */}
          <div className="unified-search-divider" />

          {/* Search input - enabled during ad hoc for FE keyword filtering */}
          <div className="unified-search-input-wrapper">
            <i className="fas fa-search unified-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="unified-search-input"
              placeholder="Cuisines or dishes..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              onBlur={() => setIsDropdownOpen(false)}
              onKeyDown={handleSearchKeyDown}
            />
            {searchText && (
              <button
                type="button"
                className="unified-search-btn unified-search-clear"
                onClick={() => {
                  setSearchText("");
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            type="button"
            className="unified-search-btn unified-search-close"
            onClick={() => setIsExpanded(false)}
            title="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Filter chips (leaf quality filters) */}
        <FilterChips />
      </div>

      {/* Suggestions dropdown */}
      {isDropdownOpen && suggestions.length > 0 && (
        <div
          className="unified-search-dropdown"
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((item, index) =>
            item.type === "header" ? (
              <div key={index} className="unified-search-dropdown-header">
                {item.label}
              </div>
            ) : item.type === "hint" ? (
              <div key={index} className="unified-search-dropdown-hint">
                <i className="fas fa-info-circle" />
                <span>{item.label}</span>
              </div>
            ) : (
              <button
                key={index}
                className={`unified-search-dropdown-item unified-search-dropdown-${item.type}`}
                onClick={() => handleSuggestionClick(item)}
              >
                <i className={`fas ${item.type === "cuisine" ? "fa-utensils" : "fa-search"}`} />
                <span>{item.label}</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  mapCenter: state.mapCenter,
  locationDisplay: state.currentLocation.locationDisplay,
  selectedCuisines: state.selectedCuisines,
  adHocSearchActive: state.restaurant.adHocSearchActive,
  restaurants: state.restaurant.restaurants,
});

export default connect(mapStateToProps, {
  updateLocation,
  fetchLocation,
  setLocationDisplay,
  updateSelectedCuisines,
})(UnifiedSearchBar);
